/**
 * 敌人（Enemies）与多阶段 Boss。移动与射击全部走内容层注入的 script/phase 脚本，
 * 机制层只做调用与状态管理。node 可直接 import（draw 才接触 renderer/Assets）。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Pool } from '../engine/pool.js';
import { Grid } from '../engine/collision.js';
import { angleTo } from '../engine/math.js';
import { L, t } from '../i18n.js';
import { spellKey, spellIndexOf } from './spellHistory.js';

/** 同屏敌人上限 */
/** 目标 uid 序列（贯通弹按目标去重用；模块级单调递增，复用不复读） */
let UID_SEQ = 1;

const MAX_ENEMIES = 80;
/** 空间分区格子边长 */
const GRID_CELL = 64;
/** 出界回收余量（顶部放宽，敌人常从版顶进入） */
const MARGIN = 48;
/** 阶段切换无敌时长（秒） */
const PHASE_INVULN = 1.5; // 宣言无敌（兼慢成型保护与可读性，护盾环见 draw）

function makeEnemy() {
  return {
    x: 0, y: 0, hp: 1, maxHp: 1, r: 12,
    spriteKey: '', scoreValue: 100,
    /** @type {null|((e: object, dt: number, ctx: object) => void)} 移动+射击脚本 */
    script: null,
    /** @type {{type: string, weight: number}[]} 掉落表 */
    drops: [],
    age: 0,
  };
}

/**
 * 小兵管理：对象池 + Grid 空间分区碰撞查询。
 */
export class Enemies {
  constructor() {
    /** @type {Pool} */
    this.pool = new Pool(MAX_ENEMIES, makeEnemy);
    this._grid = new Grid(VIEW_W, VIEW_H, GRID_CELL);
    this._out = []; // 查询复用数组，零分配
  }

  /** 当前活跃敌数 */
  get count() {
    return this.pool.activeCount;
  }

  /**
   * 生成敌人。def 字段即敌人字段，未给的用默认值。
   * @param {{x:number, y:number, hp?:number, r?:number, spriteKey?:string,
   *   scoreValue?:number, script?:?((e:object, dt:number, ctx:object)=>void),
   *   drops?:{type:string, weight:number}[]}} def
   * @returns {object|null}
   */
  spawn(def) {
    const e = this.pool.obtain();
    if (!e) return null;
    // 清上一任复用者的脚本状态键（_hold/_bye/_ft/_nextFire/_sdx 等会跨池残留——历史 bug）
    for (const k of Object.keys(e)) if (k.startsWith('_') && k !== '_active') delete e[k];
    e.x = def.x;
    e.y = def.y;
    e.hp = def.hp !== undefined ? def.hp : 1;
    e.maxHp = e.hp;
    e.r = def.r !== undefined ? def.r : 12;
    e.spriteKey = def.spriteKey || '';
    e.scoreValue = def.scoreValue !== undefined ? def.scoreValue : 100;
    e.script = def.script || null;
    e.drops = def.drops || [];
    e.age = 0;
    e._uid = UID_SEQ++;
    return e;
  }

  /**
   * 推进全部敌人：执行脚本 → 出界（左右下）回收。
   * @param {number} dt 秒
   * @param {object} ctx {field, player, items, rand, t, ...} 由 gameScene 组装
   */
  update(dt, ctx) {
    this.pool.forEachActive((e) => {
      e.age += dt;
      if (e.script) e.script(e, dt, ctx);
      if (e.x < -MARGIN || e.x > VIEW_W + MARGIN || e.y > VIEW_H + MARGIN) {
        this.pool.release(e);
      }
    });
  }

  /**
   * 对敌人造成伤害；致死时：爆效 + 掉落 + scoring.registerKill + 回收。
   * @param {object} e 敌人对象
   * @param {number} dmg 伤害
   * @param {object} ctx {effects, items, scoring, state} 由 gameScene 组装
   */
  damage(e, dmg, ctx) {
    if (!e._active) return;
    e.hp -= dmg;
    if (e.hp > 0) return;
    if (ctx.effects) ctx.effects.explosion(e.x, e.y, e.r / 12);
    if (ctx.items) ctx.items.scatterDrops(e.x, e.y, e.drops);
    if (ctx.scoring) ctx.scoring.registerKill(e.scoreValue);
    if (ctx.state) ctx.state.addSpecial(2);
    this.pool.release(e);
  }

  /**
   * 查询与圆 (x,y,r) 碰撞的活跃敌人（Grid 粗筛 + 精确圆判定）。
   * @param {number} x @param {number} y @param {number} r
   * @returns {object[]} 复用内部数组，勿跨帧持有
   */
  queryHit(x, y, r) {
    this._grid.clear();
    this.pool.forEachActive((e) => this._grid.insert(e));
    const cands = this._grid.query(x, y, r, this._out);
    let n = 0;
    for (let i = 0; i < cands.length; i++) {
      const e = cands[i];
      const rr = r + e.r;
      const dx = e.x - x;
      const dy = e.y - y;
      if (dx * dx + dy * dy <= rr * rr) cands[n++] = e;
    }
    cands.length = n;
    return cands;
  }

  /**
   * 遍历全部活跃敌人。
   * @param {(e: object) => void} fn
   */
  forEachActive(fn) {
    this.pool.forEachActive(fn);
  }

  /** 清空全部敌人。 */
  clear() {
    this.pool.clear();
  }

  /**
   * 绘制全部敌人：优先 Assets.img(spriteKey)，无素材画占位圆。
   * 小兵不画血条（主流弹幕游戏惯例：只有 boss 显示血量）。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets]
   */
  draw(renderer, assets) {
    this.pool.forEachActive((e) => {
      const img = assets && e.spriteKey ? assets.img(e.spriteKey) : undefined;
      if (img) {
        renderer.sprite(img, e.x, e.y);
      } else {
        renderer.circle(e.x, e.y, e.r, { fill: '#a55', stroke: '#fff' });
      }
    });
  }
}

/**
 * 多阶段 Boss。phases 由内容层提供：
 * {name, spellCard(bool), hp, timeLimit, enter(boss), update(boss,dt,ctx)}。
 * 阶段结束（血尽或超时）自动切下一阶段并清全场敌弹转道具；
 * 符卡阶段结束时调用 ctx.scoring.resolveSpellCard 并发 onSpellCardResult 事件。
 */
export class Boss {
  /**
   * @param {{name:string, title?:string, spriteKey?:string, x:number, y:number,
   *   phases: object[], ctx?: object, onDefeated?: ?((boss: Boss) => void),
   *   onPhaseChange?: ?((idx: number, phase: object) => void),
   *   onSpellCardResult?: ?((bonus: number) => void)}} def
   *   ctx 需含 {scoring, field, items, effects, playerX, playerY, ...}（gameScene 注入）。
   *   钩子也可构造后赋值，但首次 onPhaseChange（阶段 0）在构造时即发。
   */
  constructor({ name, title, spriteKey, x, y, phases, onDefeated, onPhaseChange, onSpellCardResult, ctx }) {
    this.name = name;
    this.title = title || '';
    this.spriteKey = spriteKey || '';
    this.x = x;
    this.y = y;
    this._uid = UID_SEQ++;
    this.r = 28;
    this.phases = phases;
    this.ctx = ctx || {};
    this.onDefeated = onDefeated || null;
    /** @type {null|((idx: number, phase: object) => void)} */
    this.onPhaseChange = onPhaseChange || null;
    /** @type {null|((bonus: number) => void)} */
    this.onSpellCardResult = onSpellCardResult || null;
    this.age = 0;
    this.alive = true;
    this.phaseIndex = -1;
    this.hp = 1;
    this.maxHp = 1;
    this.phaseTime = 0;
    this.timeLimit = 0;
    this.invulnUntil = 0;
    this._dmgTaken = 0;
    /** 本阶段是否中弹/用炸弹（符卡判定，gameScene 可置脏） */
    this.phaseDirty = false;
    /** 冲刺拖影状态（速度超阈值时记录残影，draw 用） */
    this._trail = [];
    this._lastX = x;
    this._lastY = y;
    this._speed = 0;
    this._advance();
  }

  /** 当前阶段定义 */
  get phase() {
    return this.phases[this.phaseIndex];
  }

  /** @private 当前生效的 DPS 钳顶（dpsCapFirst 窗口内用窗口值，否则 dpsCap；无则 0）。 */
  _capNow() {
    const p = this.phase;
    if (!p) return 0;
    if (p.dpsCapFirst && this.phaseTime < p.dpsCapFirst[0]) return p.dpsCapFirst[1];
    return p.dpsCap || 0;
  }

  /**
   * 对 Boss 造成伤害（无敌帧内忽略）。
   * @param {number} d 伤害
   */
  damage(d) {
    if (!this.alive || this.age < this.invulnUntil) return;
    const cap = this._capNow();
    if (cap) {
      // 慢成型符卡的 DPS 钳顶（漏桶）：有效 DPS ≤ cap——最低火力不受影响、
      // 满火力融不掉（"打得完"与"两循环"同时成立；th06 符卡减伤的钳顶形态）
      const allowed = Math.max(0, Math.min(d, cap - this._dmgTaken));
      this._dmgTaken += allowed;
      d = allowed;
    }
    this.hp -= d;
    if (this.hp <= 0) this._endPhase(false);
  }

  /**
   * 推进：阶段 update 脚本 + 时限倒计时（超时强制下阶段）。
   * @param {number} dt 秒
   */
  update(dt) {
    if (!this.alive) return;
    this.age += dt;
    this.phaseTime += dt;
    const p = this.phase;
    const capNow = this._capNow();
    if (capNow && this._dmgTaken > 0) this._dmgTaken = Math.max(0, this._dmgTaken - capNow * dt);
    if (p && p.update) p.update(this, dt, this.ctx);
    // 冲刺拖影：快速移动时记录残影轨迹（0.3s 渐隐）
    const mdx = this.x - this._lastX;
    const mdy = this.y - this._lastY;
    this._speed = dt > 0 ? Math.hypot(mdx, mdy) / dt : 0;
    this._lastX = this.x;
    this._lastY = this.y;
    if (this._speed > 90) this._trail.push({ x: this.x, y: this.y, age: 0 });
    for (const tr of this._trail) tr.age += dt;
    while (this._trail.length && this._trail[0].age > 0.3) this._trail.shift();
    if (p && p.spellCard && this.ctx.scoring && this.ctx.scoring.spellCardTick) {
      this.ctx.scoring.spellCardTick(dt);
    }
    if (p && this.phaseTime >= this.timeLimit) this._endPhase(true);
  }

  /**
   * 自机狙辅助：Boss 指向自机的角度。
   * @returns {number}
   */
  aimAtPlayer() {
    return angleTo(this.x, this.y, this.ctx.playerX || 0, this.ctx.playerY || 0);
  }

  /** @private 结束当前阶段：符卡结算（仅血尽且未超时才有奖励）→ 切阶段。 */
  _endPhase(timedOut) {
    const p = this.phase;
    if (p && p.spellCard && this.ctx.scoring) {
      const bonus = this.ctx.scoring.resolveSpellCard({
        noMiss: !this.phaseDirty,
        noBomb: !this.phaseDirty,
        inTime: !timedOut,
        timeLeft: Math.max(0, this.timeLimit - this.phaseTime),
        phaseIndex: this.phaseIndex,
      });
      if (this.onSpellCardResult) this.onSpellCardResult(bonus);
    }
    // 阶段切换：清全场敌弹转道具（收拢到 boss 处成一小簇，方便拾取）
    if (this.ctx.field && this.ctx.items) this.ctx.field.clearToItems(this.ctx.items, this.x, this.y);
    if (this.phaseIndex + 1 >= this.phases.length) {
      this.alive = false;
      if (this.onDefeated) this.onDefeated(this);
      return;
    }
    this._advance();
  }

  /** @private 进入下一阶段：初始化血量/时限/无敌帧，符卡开账，回调 enter 与事件。 */
  _advance() {
    this.phaseIndex++;
    const p = this.phase;
    this.hp = p.hp;
    this.maxHp = p.hp;
    this._dmgTaken = 0; // dpsCap 漏桶（若本阶段启用）
    this.timeLimit = p.timeLimit !== undefined ? p.timeLimit : Infinity;
    this.phaseTime = 0;
    this.phaseDirty = false;
    // 清上一阶段的脚本状态键：残余计时相位会让新阶段首发在飞行途中提前触发，
    // 游移标志不清则新阶段从旧锚点开始游摆（历史 bug）
    for (const k of Object.keys(this)) {
      if (k.startsWith('_ft') || k === '_bt' || k === '_wanderT' || k === '_atAnchor' || k === '_whirlQ') delete this[k];
    }
    this.invulnUntil = this.age + PHASE_INVULN;
    if (p.spellCard && this.ctx.scoring && this.ctx.scoring.spellCardStart) {
      this.ctx.scoring.spellCardStart(this.ctx.stageIndex || this.phaseIndex, this.timeLimit, this.ctx.difficulty || 1);
    }
    if (p.enter) p.enter(this);
    if (this.onPhaseChange) this.onPhaseChange(this.phaseIndex, p);
  }

  /**
   * 绘制 Boss：精灵/占位圆 + 顶部血条 + 阶段名 + 剩余时间。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets]
   */
  draw(renderer, assets) {
    if (!this.alive) return;
    const img = assets && this.spriteKey ? assets.img(this.spriteKey) : undefined;
    const blink = this.age < this.invulnUntil && Math.floor(this.age * 20) % 2 === 0;
    // 冲刺拖影（先画残影再画本体）
    for (const tr of this._trail) {
      const ta = 0.35 * (1 - tr.age / 0.3);
      if (ta <= 0) continue;
      if (img) renderer.sprite(img, tr.x, tr.y, { alpha: ta });
      else renderer.circle(tr.x, tr.y, this.r, { fill: '#c66', alpha: ta });
    }
    if (img) {
      renderer.sprite(img, this.x, this.y, { alpha: blink ? 0.4 : 1 });
    } else {
      renderer.circle(this.x, this.y, this.r, { fill: blink ? '#a55' : '#c66', stroke: '#fff' });
      renderer.text(L(this.name), this.x, this.y - 5, { size: 10, color: '#fff', align: 'center' });
    }
    // 宣言无敌帧护盾环：加粗脉动双环 + 慢渐隐（明示"现在打上去会弹开"）
    if (this.age < this.invulnUntil) {
      const left = this.invulnUntil - this.age;
      const rr = this.r + 14 + Math.sin(this.age * 12) * 5;
      const ringA = Math.min(1, left * 1.5 + 0.4);
      renderer.circle(this.x, this.y, rr, { stroke: '#adf', alpha: ringA, lineWidth: 3 });
      renderer.circle(this.x, this.y, rr + 8, { stroke: '#adf', alpha: ringA * 0.35, lineWidth: 2 });
    }
    // 血条 + 阶段名 + 时限（阶段名经 L() 按语言取）
    const p = this.phase;
    renderer.rect(40, 30, 400, 6, { fill: '#300' });
    renderer.rect(40, 30, 400 * Math.max(0, this.hp / this.maxHp), 6, { fill: p && p.spellCard ? '#f4c' : '#f44' });
    if (p) renderer.text(L(p.name), 40, 40, { size: 11, color: '#fcc' });
    if (this.timeLimit !== Infinity) {
      const left = Math.max(0, this.timeLimit - this.phaseTime);
      renderer.text(left.toFixed(1), 440, 18, { size: 11, color: left < 10 ? '#f88' : '#ccc', align: 'right' });
    }
    // 符卡奖励（东方风）：符卡阶段实时显示奖励面值；判脏（伤/雷/技能）则灰显 FAILED
    if (p && p.spellCard && this.ctx.scoring && typeof this.ctx.scoring.spellCardValue === 'function') {
      const failed = this.phaseDirty;
      const v = this.ctx.scoring.spellCardValue();
      renderer.text(
        failed ? 'SPELL FAILED' : `SPELL BONUS ${v}`,
        440, 52, { size: 11, color: failed ? '#888' : '#f4c', align: 'right' },
      );
    }
    // 符卡挑战历史（东方符卡列表惯例）：符卡名下方显示 取得/挑战
    if (p && p.spellCard && this.ctx.spellHistory && this.kind !== undefined) {
      const rec = this.ctx.spellHistory[spellKey(this.spellStageIndex, this.kind, spellIndexOf(this))];
      const c = rec ? rec.captured : 0;
      const a = rec ? rec.attempts : 0;
      renderer.text(`${t('hud.spellHistory')} ${c}/${a}`, 40, 52, { size: 10, color: a > 0 ? '#9bd' : '#667' });
    }
  }
}
